import { useEffect, useRef, useState } from 'react';
import heic2any from 'heic2any';
import toggleToast from '../utils/messaging';

const useImageUpload = () => {
  const [selectedLocalFile, setSelectedLocalFile] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [dimensions, setDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedLocalFile(file);
    }
  };

  useEffect(() => {
    if (selectedLocalFile) return;

    if (fileInputRef?.current) {
      fileInputRef.current.value = '';
    }
  }, [selectedLocalFile]);

  const prevDimensions = useRef<{ width: number; height: number } | null>(null);

  useEffect(() => {
    if (imageRef.current) {
      const img = imageRef.current;
      const updateDimensions = () => {
        const newDimensions = {
          width: img.naturalWidth,
          height: img.naturalHeight,
        };

        if (
          !prevDimensions.current ||
          prevDimensions.current.width !== newDimensions.width ||
          prevDimensions.current.height !== newDimensions.height
        ) {
          setDimensions(newDimensions);
          prevDimensions.current = newDimensions;
        }
      };

      img.onload = updateDimensions;
      if (img.complete) {
        updateDimensions();
      }
    }
  }, [imageRef, selectedLocalFile]);

  useEffect(() => {
    setImageUrl('');
    if (selectedLocalFile && imageRef.current) {
      getUrl(selectedLocalFile);
    }
  }, [selectedLocalFile, imageRef.current]);

  const getUrl = (file: File) => {
    console.log(':::getUrl for file : ', file);

    if (file.type === 'image/heic') {
      setImageUrl(
        "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 50 50'><path fill='gray' d='M43.935,25.145c0-10.318-8.364-18.682-18.682-18.682c-10.318,0-18.682,8.364-18.682,18.682h4.068 c0-8.071,6.543-14.614,14.614-14.614s14.614,6.543,14.614,14.614H43.935z'><animateTransform attributeType='xml' attributeName='transform' type='rotate' from='0 25 25' to='360 25 25' dur='1s' repeatCount='indefinite'/></path></svg>",
      );

      heic2any({
        blob: file,
        toType: 'image/webp',
        quality: 0.8,
      }).then((webpBlob: Blob | Blob[]) => {
        const url = URL.createObjectURL(
          Array.isArray(webpBlob) ? webpBlob[0] : webpBlob,
        );
        setImageUrl(url);
        console.log(':::url: ', url);
      });
    } else {
      const url = URL.createObjectURL(file);
      setImageUrl(url);
      console.log(':::url: ', url);
    }

    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  };

  const drawImage = (
    ctx: CanvasRenderingContext2D,
    image: HTMLImageElement | HTMLCanvasElement,
    canvas: HTMLCanvasElement,
  ) => {
    const width =
      image instanceof HTMLImageElement ? image.naturalWidth : image.width;
    const height =
      image instanceof HTMLImageElement ? image.naturalHeight : image.height;
    ctx.drawImage(
      image,
      0,
      0,
      width,
      height,
      0,
      0,
      canvas.width,
      canvas.height,
    );
  };

  const handleUpload = async ({
    selectedBooking,
    client,
    uploadImage,
    appendImage,
  }: {
    selectedBooking: any;
    client: any;
    uploadImage: (params: {
      bookingId: string;
      formData: FormData;
    }) => Promise<any>;
    appendImage: (image: any) => void;
  }) => {
    console.log(':::handleUpload...');

    if (!selectedLocalFile) return;

    try {
      const imageElem: HTMLImageElement | null = imageRef?.current || null;
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d', { alpha: false });
      const canvasThumb = document.createElement('canvas');
      const thumbCtx = canvasThumb.getContext('2d', { alpha: false });

      console.log(':::selectedFile:', selectedLocalFile);

      if (
        !imageElem ||
        imageElem.naturalWidth === 0 ||
        imageElem.naturalHeight === 0 ||
        !ctx ||
        !thumbCtx ||
        !selectedBooking ||
        !client
      ) {
        console.log(
          ':::missing required parameters...',
          imageElem,
          ctx,
          thumbCtx,
          selectedBooking,
          client,
        );
        return;
      }

      console.log(':::client:', client);
      console.log(':::selectedBooking:', selectedBooking);

      setIsUploading(true);
      const folder = `${client.sub}/${selectedBooking.booking_session_code}`;

      let scalePreview =
        1500 / Math.max(imageElem.naturalWidth, imageElem.naturalHeight);
      if (scalePreview > 1) scalePreview = 1;

      canvas.width = Math.round(imageElem.naturalWidth * scalePreview);
      canvas.height = Math.round(imageElem.naturalHeight * scalePreview);

      console.log(
        '::::scalePreview:',
        scalePreview,
        ', canvas.width:',
        canvas.width,
        ', canvas.height:',
        canvas.height,
        ', imageElem.naturalWidth:',
        imageElem.naturalWidth,
        ', imageElem.naturalHeight:',
        imageElem.naturalHeight,
      );

      const id = Date.now().toString();
      const metadata = {
        id,
        width: canvas.width,
        height: canvas.height,
        src: `${folder}/${id}.webp`,
        thumb: {
          width: canvasThumb.width,
          height: canvasThumb.height,
          src: `${folder}/${id}-thumb.webp`,
        },
        layer: 0,
        strokes: [[]],
      };

      console.log('::::metadata:', metadata);

      const formData = new FormData();
      formData.append('metadata', JSON.stringify(metadata));

      ctx.globalCompositeOperation = 'copy';
      drawImage(ctx, imageElem, canvas);

      const imageBlob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob((blob) => resolve(blob), 'image/webp', 0.8);
      });

      if (imageBlob) {
        formData.append('image_file', imageBlob, `${id}.webp`);
      }

      console.log(':::::imageBlob:', imageBlob);

      const scaleThumb = 300 / Math.max(imageElem.width, imageElem.height);

      canvasThumb.width = Math.round(canvas.width * scaleThumb);
      canvasThumb.height = Math.round(canvas.height * scaleThumb);

      thumbCtx.globalCompositeOperation = 'copy';
      drawImage(thumbCtx, canvas, canvasThumb);

      const thumbBlob = await new Promise<Blob | null>((resolve) => {
        canvasThumb.toBlob((blob) => resolve(blob), 'image/webp', 0.8);
      });

      if (thumbBlob) {
        formData.append('thumb_file', thumbBlob, `${id}-thumb.webp`);
      }

      console.log(':::::formData:', formData);

      const image = await uploadImage({
        bookingId: selectedBooking.booking_id,
        formData,
      });

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      canvas.width = 0;
      canvas.height = 0;

      thumbCtx.clearRect(0, 0, canvasThumb.width, canvasThumb.height);
      canvasThumb.width = 0;
      canvasThumb.height = 0;

      console.log(':::::image:', image);
      setIsUploading(false);
      setSelectedLocalFile(null);

      if (!image) {
        setIsUploading(false);
        console.log(':::::image upload failed...');
        return toggleToast?.('Upload failed.', 'error');
      }

      appendImage(image);
    } catch (error) {
      console.log(error);
    }
  };

  return {
    selectedLocalFile,
    imageUrl,
    fileInputRef,
    imageRef,
    dimensions,
    handleFileChange,
    setSelectedLocalFile,
    handleUpload,
    isUploading,
    setIsUploading,
  };
};

export default useImageUpload;
